import React, { memo, useEffect, useState } from 'react'
import { ISuggestedValuesProps } from '../../../../../SuggestedValues/SuggestedValues'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import {
    selectRuleConfigBit, selectRuleFullPath,
    selectRuleIsChecked,
} from '../../../../../../../store/selectors/ruleCardSelectors'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getLabel } from '../../../../../../../utils/getLabel'
import { stringInputErrors } from '../../../../../../../utils/validationErrors/stringInputErrors'
import { initInputData } from '../../../../../../../utils/InitInputData'
import { inputDataDispatcher } from '../../../../../../../utils/InputDataDispatcher'
import {
    buildControlsExt,
    inputWithSuggestionsDropdown,
} from '../../../../../../inputs/controls'
import { selectConfigBit } from '../../../../../../../store/selectors/configSelectors'

export const RuleSuggestedValues = memo(function RuleSuggestedValues({
    dataPath,
    pathWithElementID,
    dataSchema,
    name,
    wrapper,
}: ISuggestedValuesProps) {
    const dispatch = useAppDispatch()
    const pathSplit = dataPath.split(PATH_JOINER)
    const configBit = useAppSelector(selectRuleConfigBit(pathSplit))
    const path = [...pathSplit, name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectRuleIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        default: defaultValue,
    } = dataSchema
    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit || {}, { [name]: validationCheck })

    const fullPath = useAppSelector(selectRuleFullPath)
    const [guid] = fullPath

    const suggestedValuesPath = dataSchema.suggested_values_path ?? ''
    const suggestedValuesPathArr = suggestedValuesPath.split('.')
    const suggestedValuesPathFull = [guid, 'configuration', ...suggestedValuesPathArr]
    const suggestedValuesItems = useAppSelector(selectConfigBit(suggestedValuesPathFull))

    const className: string = !wrapper ? 'w-50 pe-2' : ''
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const label: string = getLabel(name, inputLabel)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined
    const errMsg = stringInputErrors(inputState?.[name], dataSchema)

    const values = Array.isArray(suggestedValuesItems)
        ? suggestedValuesItems.reduce((acc, item) => {
              if (item && item.rule_name) acc.push(item.rule_name)
              return acc
          }, [])
        : []

    const options = Array.from(new Set(values)) as unknown as string[]

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    guid,
                    errorKey,
                    data: configBit,
                    setInputState,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isChecked && dataPath.length) {
            isValid()
        }
    }, [isChecked, dataPath.length]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit, dataPath.length])

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    guid,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                    isRuleCard: true,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                guid,
                errorKey,
                isUndefined,
                checkPath,
                type,
                isRuleCard: true,
            },
            dispatch
        )
        setTouched && setTouched((prev: any) => ({ ...prev, [name]: true }))
    }

    const controlDescriptors = [
        inputWithSuggestionsDropdown({
            name,
            options,
            errMsg,
            tooltipText,
            placeholder: defaultValue,
            label,
            className,
            isColumn: true,
            isRequired,
            onBlur: handleOnBlur,
        }),
    ]

    const SuggestedValuesCombo = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return (
        <div className={`${!wrapper ? 'w-100' : ''}`}>
            {SuggestedValuesCombo}
        </div>
    )
})
